import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AddressIcon, CloseIcon } from '../../../../components/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { resetMapState, sendGeocodeAddress, setAddressGeocodeObject, setGeocodeAddress, setGeocodeCoords, setIsHouse, setSuggestions } from '../../../../redux/reducer/slices/mapSlice';
import Loader from '../../../../components/Loader';

const SearchBar = ({ onClick }) => {
    const dispatch = useDispatch();
    const { suggestions, sendGeocodeAddressStatus, sendGeocodeCoordsStatus, geocodeAddress, addressGeocodeObject, isInsidePolygon } = useSelector((state) => state.map);
    const [value, setValue] = useState('');
    const inputRef = useRef(null);
    const isUserTypingRef = useRef(false);
    const preventBlurSearchRef = useRef(false);
    const lastSearchedRef = useRef('');

    const formattedAddress = useMemo(() => {
        if (!addressGeocodeObject) return '';

        const parts = [];
        if (addressGeocodeObject.locality) parts.push(addressGeocodeObject.locality);
        if (addressGeocodeObject.street) parts.push(addressGeocodeObject.street);
        if (addressGeocodeObject.house) parts.push(addressGeocodeObject.house);
        return parts.join(', ');
    }, [addressGeocodeObject]);

    const handleClear = useCallback(() => {
        isUserTypingRef.current = false;
        lastSearchedRef.current = '';
        dispatch(resetMapState());
        setValue('');
    }, [dispatch]);

    useEffect(() => {
        // Обновляем input из стора только когда адрес реально обновился
        // (например после геокода или после движения карты), и не перетираем ручной ввод.
        if (isUserTypingRef.current) return;

        if (!geocodeAddress) {
            setValue('');
            return;
        }

        if (formattedAddress) {
            setValue(formattedAddress);
        }
    }, [geocodeAddress, formattedAddress]);

    const handleInputChange = (e) => {
        isUserTypingRef.current = true;
        setValue(e.target.value);
        if (!e.target.value) {
            handleClear();
        }
    }

    const handleSearch = () => {
        const query = value.trim();
        if (query === '') return;
        if (query === lastSearchedRef.current) return;

        isUserTypingRef.current = false;
        lastSearchedRef.current = query;
        dispatch(sendGeocodeAddress(query));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handleFocus = () => {
        // dispatch(setMapZoom(10));
    };

    const handleBlur = () => {
        // Если пользователь кликает по подсказке — blur прилетает раньше click
        // поэтому блокируем автопоиск на короткое время.
        setTimeout(() => {
            if (preventBlurSearchRef.current) {
                preventBlurSearchRef.current = false;
                return;
            }

            if (suggestions.length === 0) {
                handleSearch();
            }
        }, 150);
    };

    const handleSuggestionClick = (geoAddress, geoCoords, addressGeocodeObject, isHouse) => {
        isUserTypingRef.current = false;
        dispatch(setGeocodeAddress(geoAddress))
        dispatch(setGeocodeCoords(geoCoords))
        dispatch(setAddressGeocodeObject(addressGeocodeObject))
        dispatch(setIsHouse(isHouse));
        dispatch(setSuggestions([]));
        const parts = [];
        if (addressGeocodeObject?.locality) parts.push(addressGeocodeObject.locality);
        if (addressGeocodeObject?.street) parts.push(addressGeocodeObject.street);
        if (addressGeocodeObject?.house) parts.push(addressGeocodeObject.house);
        const nextValue = parts.length ? parts.join(', ') : geoAddress;
        setValue(nextValue);

        if (inputRef.current) {
            inputRef.current.focus();
            const length = nextValue.length;
            inputRef.current.selectionStart = length;
            inputRef.current.selectionEnd = length;
        }
    };

    return (
        <SearchWrapper>
            <AddressIconWrapper>
                <AddressIcon/>
            </AddressIconWrapper>
            <Input
                ref={inputRef}
                type='text'
                placeholder='Введите адрес'
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            {value && <ClearButton onClick={handleClear}><CloseIcon width={24} height={24}/></ClearButton>}
            <SearchButton onClick={onClick} disabled={!addressGeocodeObject || !isInsidePolygon}>
                {sendGeocodeAddressStatus === 'pending' || sendGeocodeCoordsStatus === 'pending' ?
                    <Loader/>
                :
                    'Далее'
                }
            </SearchButton>

            {suggestions && suggestions.length > 1 && (
                <SuggestionsList>
                    {suggestions.map((suggestion, index) => (
                        <SuggestionItem
                            key={index}
                            onMouseDown={() => { preventBlurSearchRef.current = true; }}
                            onClick={() => handleSuggestionClick(suggestion.name, suggestion.coords, suggestion.addressObj, suggestion.isHouse)}
                        >
                            {suggestion.addressObj.fullName}
                        </SuggestionItem>
                    ))}
                </SuggestionsList>
            )}
        </SearchWrapper>
    );
};

export default SearchBar;

const SearchWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    height: 60px;
    padding-left: 16px;
    border: 2px solid #E0E0E0;
    border-radius: 20px;
    background-color: #ffffff;
    width: 100%;
    max-width: 900px;
    pointer-events: all;
    /* allow flex children to shrink below their content width */
    & > * {
        min-width: 0;
    }
    @media (max-width: 748px) {
        padding-left: 8px;
        height: 50px;
        border-radius: 16px;
        border: 2px solid #EAEAEA;
        background-color: #F9F9F9;
    }
`;

const AddressIconWrapper = styled.div`
    @media (max-width: 748px) {
        display: none;
    }
`;

const Input = styled.input`
    flex: 1 1;
    width: 100%;
    border: none;
    outline: none;
    padding: 0 16px;
    font-size: 16px;
    color: #333;
    background-color: transparent;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    @media (max-width: 748px) {
        padding: 0 8px;
    }
`;

const ClearButton = styled.button`
    flex: 0 0 24px;
    height: 20px;
    background: none;
    border: none;
    font-size: 18px;
    line-height: 1;
    color: #999;
    cursor: pointer;
    margin-right: 8px;

    &:hover {
        color: #333;
    }
`;

const SearchButton = styled.button`
    margin-right: -1px;
    padding: 0 16px;
    flex: 0 0 120px;
    height: 60px;
    background: ${({disabled}) => disabled ? '#cccccc' : '#EA394B'};
    border-radius: 19px;
    border: solid 1px ${({disabled}) => disabled ? '#cccccc' : '#EA394B'};
    font-size: 15px;
    line-height: 1;
    font-weight: 500;
    color: #ffffff;
    cursor: ${({disabled}) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s;
    &:hover {
        background: ${({disabled}) => disabled ? '#cccccc' : '#d73636'};
    }
    @media (max-width: 748px) {
        flex: 0 0 90px;
        height: 50px;
        padding: 0 8px;
    }
`;

const SuggestionsList = styled.ul`
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: #ffffff;
    border: 1px solid #ddd;
    border-top: none;
    box-shadow: 0px 20px 24px 0px #00000029;
    border-radius: 16px;
    margin-top: 5px;
    padding: 0;
    list-style-type: none;
    max-height: 200px;
    overflow-y: auto;
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    @media (max-width: 748px) {
        top: unset;
        bottom: 100%;
        margin-bottom: 5px;
        flex-direction: column-reverse;
        box-shadow: 0px 0px 24px 0px #00000029;
    }
`;

const SuggestionItem = styled.li`
    padding: 10px;
    cursor: pointer;
    font-size: 16px;
    color: #333;

    &:hover {
        background-color: #f5f5f5;
    }
`;
